import { useMemo } from 'react'
import EventStation from '../../components/captain/EventStation'
import RoleSlotPill from '../../components/captain/RoleSlotPill'
import RosterTable, { type RosterEntryRow } from '../../components/captain/RosterTable'
import EmptyState from '../../components/captain/EmptyState'
import type { MarkerState } from '../../components/captain/StationMarker'
import type { CaptainEvent, CaptainRole } from '../../types/captain'
import CaptainView from './CaptainView'

interface Props {
  captainId: string
  events: CaptainEvent[]
  pastEvents: CaptainEvent[]
  /** event_id → signup_id, for cancel / change role RPCs. */
  mySignupIdByEventId: Record<string, string>
  onSignup: (eventId: string, role: CaptainRole) => Promise<string | null>
  onCancel: (signupId: string) => Promise<string | null>
  onChangeRole: (signupId: string, newRole: CaptainRole) => Promise<string | null>
  /** Per-event roster from list_event_captain_roster (real mode) or derived from mock data. */
  rostersByEventId: Record<string, RosterEntryRow[]>
  /** ON-426 deep link — see CaptainView. */
  autoOpenEventId?: string | null
}

// ON-334: 'full' here means "target met" — informational for planning, not a
// closed state; overflow sign-ups are still accepted past this point.
// Checked per-role, not aggregate: an aggregate totalFilled >= totalTarget can be
// true even when one role is short, if another role's overflow makes up the
// difference (e.g. pacer 4/2, sweeper 0/2 -> aggregate 4/4 but sweeper needs 2).
function markerStateFor(event: CaptainEvent): MarkerState {
  const totalFilled = event.slots.reduce((a, s) => a + s.filledBy.length, 0)
  if (totalFilled === 0) return 'open'
  const allRolesMet = event.slots.every((s) => s.filledBy.length >= s.target)
  return allRolesMet ? 'full' : 'partial'
}

/**
 * Organizers are captains too — they see the exact same sign-up surface a
 * regular captain gets (upcoming events + my sign-ups), with the club
 * management controls moved onto their own "Manage" tab. The backend already
 * allows this: signup_for_captain_role / change_captain_role /
 * cancel_captain_signup are all scoped to auth.uid() with no organizer
 * exclusion, so no RPC change was needed.
 */
export default function OrganizerView({
  captainId,
  events,
  pastEvents,
  mySignupIdByEventId,
  onSignup,
  onCancel,
  onChangeRole,
  rostersByEventId,
  autoOpenEventId,
}: Props) {
  return (
    <CaptainView
      captainId={captainId}
      upcomingEvents={events}
      pastEvents={pastEvents}
      mySignupIdByEventId={mySignupIdByEventId}
      onSignup={onSignup}
      onCancel={onCancel}
      onChangeRole={onChangeRole}
      autoOpenEventId={autoOpenEventId}
      extraTabs={[
        {
          id: 'manage',
          label: 'Manage',
          meta: String(events.length).padStart(2, '0'),
          eyebrow: 'Club-wide captain coverage',
          content: (
            <ManagementPanel events={events} rostersByEventId={rostersByEventId} />
          ),
        },
      ]}
    />
  )
}

interface ManagementPanelProps {
  events: CaptainEvent[]
  rostersByEventId: Record<string, RosterEntryRow[]>
}

function ManagementPanel({ events, rostersByEventId }: ManagementPanelProps) {
  const stats = useMemo(() => {
    let totalTarget = 0
    let totalFilled = 0
    let stillNeeded = 0
    let overflow = 0
    for (const e of events) {
      for (const s of e.slots) {
        const filled = s.filledBy.length
        totalTarget += s.target
        totalFilled += filled
        // Sum per-role, not per-event/aggregate — a role's overflow must
        // never offset another role's shortfall (ON-334: per-role tracking).
        stillNeeded += Math.max(s.target - filled, 0)
        overflow += Math.max(filled - s.target, 0)
      }
    }
    // Distinct captains across all events. The roster RPC doesn't expose user
    // ids, so we dedupe on display_name; rows without a name still count as
    // one anonymous slot each (rare edge case, and an undercount beats the
    // previous always-0-or-1 reading from sentinel placeholders).
    const captainKeys = new Set<string>()
    let anonymousCount = 0
    for (const eventId of Object.keys(rostersByEventId)) {
      for (const entry of rostersByEventId[eventId]) {
        if (entry.displayName) captainKeys.add(entry.displayName)
        else anonymousCount += 1
      }
    }
    return {
      totalTarget,
      totalFilled,
      stillNeeded,
      overflow,
      activeCaptains: captainKeys.size + anonymousCount,
    }
  }, [events, rostersByEventId])

  if (events.length === 0) {
    return (
      <EmptyState
        eyebrow="No flagged events"
        title="None of your upcoming events need captains yet."
        body="Toggle “Needs captains” on an event in the Stazon mobile app to start collecting volunteers."
      />
    )
  }

  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 12,
          marginBottom: 40,
        }}
        className="cp-cascade"
      >
        <StatBlock
          label="Roles filled"
          value={`${stats.totalFilled}`}
          hint={`of ${stats.totalTarget} needed`}
        />
        <StatBlock
          label="Still needed"
          value={`${stats.stillNeeded}`}
          accent={stats.stillNeeded === 0 ? 'lime' : 'pink'}
        />
        <StatBlock
          label="Overflow"
          value={`+${stats.overflow}`}
          accent={stats.overflow > 0 ? 'lime' : undefined}
        />
        <StatBlock label="Active captains" value={`${stats.activeCaptains}`} />
        <StatBlock label="Events" value={`${events.length}`} hint="upcoming" />
      </div>

      <SectionEyebrow>Per-event roster</SectionEyebrow>

      <div
        className="cp-cascade cp-layout"
        style={{ display: 'flex', flexDirection: 'column', gap: 0 }}
      >
        {events.map((event, idx) => {
          const state = markerStateFor(event)
          const totalTarget = event.slots.reduce((a, s) => a + s.target, 0)
          const totalFilled = event.slots.reduce((a, s) => a + s.filledBy.length, 0)
          // Sum per-role, not aggregate subtraction: a role's overflow must never
          // cancel out another role's shortfall (ON-334: per-role tracking).
          const totalOverflow = event.slots.reduce(
            (a, s) => a + Math.max(s.filledBy.length - s.target, 0),
            0,
          )
          return (
            <EventStation
              key={event.id}
              event={event}
              markerState={state}
              isFirst={idx === 0}
              isLast={idx === events.length - 1}
            >
              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  flexWrap: 'wrap',
                  marginBottom: 22,
                  alignItems: 'center',
                }}
              >
                {event.slots.map((slot) => {
                  const filled = slot.filledBy.length
                  const overflow = Math.max(filled - slot.target, 0)
                  const meta =
                    overflow > 0
                      ? `${filled} of ${slot.target} · +${overflow} extra`
                      : `${filled} of ${slot.target} needed`
                  return (
                    <RoleSlotPill
                      key={slot.role}
                      role={slot.role}
                      variant={filled >= slot.target && slot.target > 0 ? 'met' : 'open'}
                      meta={meta}
                    />
                  )
                })}
                {/* ON-334: organizer's full picture — target, filled, and overflow together. */}
                <span
                  style={{
                    marginLeft: 'auto',
                    fontFamily:
                      "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
                    fontSize: 11,
                    color: state === 'full' ? '#C2E530' : 'rgba(198,196,185,0.7)',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {totalOverflow > 0
                    ? `${totalFilled} of ${totalTarget} needed ✓ +${totalOverflow} extra`
                    : `${totalFilled} of ${totalTarget} needed`}
                </span>
              </div>
              <RosterTable
                slots={event.slots.map((s) => ({ role: s.role, target: s.target }))}
                entries={rostersByEventId[event.id] ?? []}
              />
            </EventStation>
          )
        })}
      </div>
    </>
  )
}

function SectionEyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontFamily: "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
        fontSize: 10,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.14em',
        color: 'rgba(198,196,185,0.6)',
        marginBottom: 28,
      }}
    >
      {children}
    </div>
  )
}

function StatBlock({
  label,
  value,
  hint,
  accent,
}: {
  label: string
  value: string
  hint?: string
  accent?: 'lime' | 'pink'
}) {
  const valueColor =
    accent === 'pink' ? '#EF606A' : accent === 'lime' ? '#C2E530' : '#F4F2F2'
  return (
    <div
      className="cp-hairline-left"
      style={{
        background: '#141414',
        border: '0.5px solid rgba(244,242,242,0.06)',
        borderRadius: 8,
        padding: '18px 18px 18px 22px',
      }}
    >
      <div
        style={{
          fontFamily: "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
          fontSize: 9,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          color: 'rgba(198,196,185,0.55)',
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span
          style={{
            fontFamily:
              "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
            fontSize: 28,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: valueColor,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {value}
        </span>
        {hint && (
          <span
            style={{
              fontFamily:
                "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
              fontSize: 11,
              color: 'rgba(198,196,185,0.5)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {hint}
          </span>
        )}
      </div>
    </div>
  )
}
